import React, { useState, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { ReactComponent as NavigationRightIcon } from '@repo/common/assets/icons/16px/icon_16px_navigation-right.svg';
import { ReactComponent as NoticeAccentIcon } from '@repo/common/assets/icons/16px/icon_16px_notice-accent.svg';
import EvidenceModal from '../EvidenceModal/EvidenceModal';

const PATHFINDER_ASSET_BASE = `${(process.env.REACT_APP_ASSETS_URL || 'https://oinglab-react-deploy.s3.ap-northeast-2.amazonaws.com/images').replace(/\/$/, '')}/Pathfinder`;
const ImgBgWhite = `${PATHFINDER_ASSET_BASE}/dashboardFile-white.svg`;
const ImgBgGreen = `${PATHFINDER_ASSET_BASE}/dashboardFile-green.svg`;

const mergeEvidenceFiles = (existingFiles = [], incomingFiles = []) => {
  if (!incomingFiles.length) return existingFiles;

  const incomingKeys = new Set(
    incomingFiles.map((file) => file.persistedId || file.key || file.id).filter(Boolean)
  );
  const incomingSignatures = new Set(
    incomingFiles.map((file) => `${file.fileName || file.file?.name || ''}:${file.file?.size || ''}`)
  );
  const localFiles = existingFiles.filter((file) => {
    const key = file.persistedId || file.key || file.id;
    const signature = `${file.fileName || file.file?.name || ''}:${file.file?.size || ''}`;
    const isLocal = !file.persistedId && (file.status !== 'success' || file.key);
    return isLocal && !incomingKeys.has(key) && !incomingSignatures.has(signature);
  });

  return [...incomingFiles, ...localFiles];
};

// ─── StatusChip ──────────────────────────────────────────────────────────────
function StatusChip({ label, inactive = false }) {
  return (
    <div className='bg-[rgba(23,20,16,0.4)] border border-[#908d88] border-solid flex gap-[2px] h-[24px] items-center justify-center pl-[8px] pr-[6px] py-[4px] rounded-[8px] shrink-0'>
      <NoticeAccentIcon
        className={twMerge('shrink-0 size-[16px]', inactive && 'grayscale')}
      />
      <p className='leading-[1.4] shrink-0 text-[12px] font-medium text-[#f9f7f4] whitespace-nowrap'>
        {label}
      </p>
    </div>
  );
}

// ─── MoreBadge ───────────────────────────────────────────────────────────────
function MoreBadge({ count }) {
  return (
    <div className='bg-[rgba(23,20,16,0.4)] border border-[#908d88] border-solid flex h-[24px] items-center justify-center px-[6px] py-[4px] rounded-[8px] shrink-0'>
      <p className='leading-[1.4] shrink-0 text-[12px] font-medium text-[#f9f7f4] text-center whitespace-nowrap'>
        +{count} more
      </p>
    </div>
  );
}

// ─── EvidenceCard ─────────────────────────────────────────────────────────────
/**
 * @param {'white'|'green'} color
 * @param {string} title
 * @param {Array<{label:string, inactive?:boolean}>} [chips]
 * @param {number} [moreCount]
 * @param {string} [statusText]   shown when chips is empty
 * @param {string} secondaryLabel
 * @param {string} primaryLabel
 * @param {function} [onSecondaryClick]
 * @param {function} [onPrimaryClick]
 * @param {string} [className]
 */
function EvidenceCard({
  color = 'white',
  title = 'Title text',
  chips,
  moreCount,
  statusText,
  secondaryLabel = 'Secondary',
  primaryLabel   = 'Primary',
  onSecondaryClick,
  onPrimaryClick,
  evidenceItems,
  sessionId,
  initialScore,
  onEvidenceSave,
  className,
}) {
  const isGreen  = color === 'green';
  const hasChips = Array.isArray(chips) && chips.length > 0;
  const [isEvidenceModalOpen, setIsEvidenceModalOpen] = useState(false);
  const [localItems, setLocalItems] = useState(evidenceItems || []);

  useEffect(() => {
    if (!evidenceItems) return;
    setLocalItems((prev) => {
      const prevMap = new Map(prev.map((item) => [item.id, item]));
      return evidenceItems.map((newItem) => {
        const existing = prevMap.get(newItem.id);
        if (!existing) return newItem;
        const hasLocalLinks = Array.isArray(existing.links) && existing.links.length > 0;
        return {
          ...newItem,
          files: mergeEvidenceFiles(existing.files ?? [], newItem.files ?? []),
          links: hasLocalLinks ? existing.links : (newItem.links ?? []),
          fileName: existing.fileName || newItem.fileName,
        };
      });
    });
  }, [evidenceItems]);

  const handleItemSaved = (savedItem) => {
    setLocalItems((prev) =>
      prev.map((item) => (item.id === savedItem.id ? savedItem : item))
    );
    onEvidenceSave?.(savedItem);
  };

  const handlePrimaryClick = () => {
    onPrimaryClick?.();
  };

  const handleCardSelect = () => {
    setIsEvidenceModalOpen(true);
  };

  return (
    <>
      {/* Outer card */}
    <div
      role='button'
      tabIndex={0}
      onClick={handleCardSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardSelect();
        }
      }}
      className={twMerge(
        'tabletV:drop-shadow-[0px_4px_8px_rgba(104,101,97,0.08),0px_16px_20px_rgba(104,101,97,0.08)]',
        'flex flex-col gap-[10px] items-start justify-end',
        'overflow-clip relative rounded-[40px] size-full cursor-pointer',
        className
      )}
    >
      {/* ── Inner border square (Figma: aspect-[280/280], centered, overflow-clip) ── */}
      <div
        className={twMerge(
          '-translate-y-1/2 absolute aspect-[280/280]',
          'border-2 border-solid left-0 overflow-clip right-0 rounded-[40px] top-1/2',
          isGreen ? 'border-[rgba(126,234,201,0.3)]' : 'border-[rgba(236,233,228,0.4)]'
        )}
      >
        {/* Backdrop blur fill */}
        <div
          aria-hidden='true'
          className={twMerge(
            'absolute backdrop-blur-[15px] inset-0 pointer-events-none rounded-[40px]',
            isGreen ? 'bg-[rgba(126,234,201,0.8)]' : 'bg-[rgba(236,233,228,0.6)]'
          )}
        />

        {/* ── Folder illustration — display:contents wrapper, children position on border div ── */}
        {/* Tilted translucent back card */}
        <div
	          className='absolute flex inset-[6.43%_5.83%_15%_10.33%] items-center justify-center'
          style={{ containerType: 'size' }}
        >
          <div
            className='flex-none rounded-[15px] backdrop-blur-[25px] bg-[rgba(255,255,255,0.4)]'
            style={{
              width:     'hypot(86.6105cqw, 11.329cqh)',
              height:    'hypot(13.3895cqw, 88.671cqh)',
              transform: 'rotate(6.99deg) skewX(-2.16deg)',
            }}
          />
        </div>

        {/* White front card — sibling of tilted card, both relative to border div */}
        <div className='absolute bg-white inset-[11.55%_14.8%_18.1%_11.88%] rounded-[15px]' />

        {/* Background gradient image */}
        <div className='absolute inset-[calc(28.57%-0.86px)_-2px_calc(0%-2px)_-2px]'>
          <div className='absolute inset-[-0.8%_-0.57%]'>
            <img
              alt=''
              className='block max-w-none size-full'
              src={isGreen ? ImgBgGreen : ImgBgWhite}
            />
          </div>
        </div>

        {/* Inner glow shadow */}
        <div
          className={twMerge(
            'absolute inset-0 pointer-events-none rounded-[inherit]',
            isGreen
              ? 'shadow-[inset_0px_15px_15px_0px_rgba(77,213,163,0.3),inset_0px_10px_30px_0px_rgba(77,213,163,0.2)]'
              : 'shadow-[inset_0px_15px_15px_0px_rgba(213,210,205,0.25),inset_0px_10px_30px_0px_rgba(213,210,205,0.1)]'
          )}
        />
      </div>

      {/* ── Content area ── */}
      <div className='flex flex-col gap-[20px] items-start justify-end min-h-[180px] min-w-0 px-6 py-[24px] relative shrink-0 w-full tabletV:px-[30px]'>

        {/* Title + chips/text */}
        <div className='flex flex-col gap-[12px] items-start min-h-[80px] relative shrink-0 w-full'>

          {/* Title */}
          <div
            className={twMerge(
              'leading-[0] relative shrink-0 text-[20px] font-medium w-full min-w-0',
              isGreen ? 'text-[#004429]' : 'text-[#171410]'
            )}
          >
            <p className='leading-[1.2] mb-0'>{title}</p>
          </div>

          {/* Chips row */}
          {hasChips && (
            <div className='flex flex-wrap gap-[8px] items-start relative shrink-0 w-full min-w-0'>
              {chips.map((chip, i) => (
                <StatusChip key={i} label={chip.label} inactive={chip.inactive} />
              ))}
              {moreCount > 0 && <MoreBadge count={moreCount} />}
            </div>
          )}

          {/* Status text (shown when no chips) */}
          {!hasChips && statusText && (
            <p
              className={twMerge(
                'leading-[1.4] relative shrink-0 text-[14px] font-medium w-full',
                isGreen ? 'text-[#009e4f]' : 'text-[#908d88]'
              )}
            >
              {statusText}
            </p>
          )}
        </div>

        {/* Buttons */}
        <div className='flex min-w-0 gap-[8px] items-start justify-end overflow-hidden relative shrink-0 w-full'>

          {/* Secondary button */}
          <div
            role='button'
            tabIndex={0}
            onClick={onSecondaryClick}
            onKeyDown={(e) => e.key === 'Enter' && onSecondaryClick?.()}
            className={twMerge(
              'flex h-[38px] min-w-0 items-center justify-center px-[12px] py-[10px] relative rounded-[8px] shrink-0 cursor-pointer',
              isGreen ? 'bg-[rgba(0,158,79,0.1)]' : 'bg-[rgba(85,82,78,0.1)]'
            )}
          >
            <p className='min-w-0 truncate leading-[1.4] font-medium text-[14px] text-[#55524e] text-center whitespace-nowrap'>
              {secondaryLabel}
            </p>
          </div>

          {/* Primary button */}
          <div
            role='button'
            tabIndex={0}
            onClick={handlePrimaryClick}
            onKeyDown={(e) => e.key === 'Enter' && handlePrimaryClick()}
            className={twMerge(
              'flex min-w-0 gap-[4px] h-[38px] items-center justify-center pl-[12px] pr-[8px] py-[10px] relative rounded-[8px] shrink cursor-pointer',
              isGreen ? 'bg-[rgba(0,158,79,0.1)]' : 'bg-[rgba(85,82,78,0.1)]'
            )}
          >
            <p className='min-w-0 truncate leading-[1.4] font-medium text-[14px] text-[#55524e] whitespace-nowrap'>
              {primaryLabel}
            </p>
            <NavigationRightIcon className='shrink-0 size-[16px] text-[#55524e]' />
          </div>

        </div>
      </div>
    </div>
    <EvidenceModal
      isOpen={isEvidenceModalOpen}
      items={localItems}
      sessionId={sessionId}
      initialScore={initialScore}
      onSave={handleItemSaved}
      onClose={() => setIsEvidenceModalOpen(false)}
    />
    </>
  );
}

export default EvidenceCard;
